import { createContext, FC, ReactNode, useContext, useState, useEffect } from 'react';
import { TemplateEditorMode } from '@global/types';

interface TemplateEditorModeContextProps {
    editorMode: TemplateEditorMode;
    setEditorMode: (mode: TemplateEditorMode) => void;
}

const TemplateEditorModeContext = createContext<TemplateEditorModeContextProps | undefined>(undefined);

export const TemplateEditorModeProvider: FC<{ children: ReactNode; initialMode?: TemplateEditorMode }> = ({
    children,
    initialMode = 'template',
}) => {
    const parentContext = useTemplateEditorModeSafe();

    const [editorMode, setEditorMode] = useState<TemplateEditorMode>(parentContext?.editorMode || initialMode);

    useEffect(() => {
        if (parentContext?.editorMode) {
            setEditorMode(parentContext.editorMode);
        }
    }, [parentContext?.editorMode]);

    return (
        <TemplateEditorModeContext.Provider value={{ editorMode, setEditorMode }}>
            {children}
        </TemplateEditorModeContext.Provider>
    );
};

export const useTemplateEditorMode = () => {
    const context = useContext(TemplateEditorModeContext);
    if (!context) {
        throw new Error('useTemplateEditorMode must be used within a TemplateEditorModeProvider');
    }
    return context;
};

export const useTemplateEditorModeSafe = () => {
    const context = useContext(TemplateEditorModeContext);
    return context;
};
